/**
 * MarineIcon — domain-specific SVG icon system for MCC.
 *
 * Replaces generic emoji + Unicode glyphs (🌍, ✦, 🛡️, 🔗, 🤝, 🧬…)
 * with vector-based marine, scientific and registry-focused symbols.
 *
 * All icons are 24x24, stroke-based, currentColor — they inherit the
 * surrounding text color and scale crisply at any size.
 */
import * as React from 'react';

export type MarineIconName =
  | 'bathymetric'      // ocean contour lines — generic ocean reference
  | 'methodology'      // layered scientific protocol
  | 'registry'         // ledger / serial-number tracking
  | 'verification'     // independent audit / VVB
  | 'monitoring'       // satellite + waveform
  | 'biodiversity'     // ecosystem connections
  | 'mangrove'         // coastal habitat (F01)
  | 'kelp'             // submerged habitat (F02)
  | 'seagrass'         // submerged habitat (F03)
  | 'coastal'          // coastal weathering / saltmarsh (F04/F05)
  | 'permanence'       // buffer pool / ratchet
  | 'transparency'     // public ledger
  | 'safeguards'       // FPIC / community
  | 'data'             // measurement / sensors
  | 'flow'             // project lifecycle
  | 'currents'         // ocean current
  | 'reversal'         // habitat-loss alert
  | 'attest'           // formal verification opinion
  | 'serial'           // unique credit serial
  | 'governance';      // independent panel

interface MarineIconProps extends React.SVGProps<SVGSVGElement> {
  name: MarineIconName;
  size?: number | string;
  /** stroke width override; defaults to 1.5 for institutional feel */
  strokeWidth?: number;
}

export function MarineIcon({ name, size = 24, strokeWidth = 1.5, ...rest }: MarineIconProps) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false as const,
    ...rest,
  };

  switch (name) {
    case 'bathymetric':
      // Concentric ocean depth contours
      return (
        <svg {...common}>
          <path d="M2 18c2-1.5 4.5-1.5 6 0s3.5 1.5 6 0 4-1.5 6 0" />
          <path d="M3 14c2-1.2 4-1.2 5.5 0s3.5 1.2 5 0 3-1.2 5-0.2" opacity="0.7" />
          <path d="M5 10c1.5-1 3-1 4 0s2.5 1 4 0 2.5-1 4 0" opacity="0.5" />
          <path d="M7 6c1-0.7 2-0.7 3 0s2 0.7 3 0 2-0.7 3-0.2" opacity="0.35" />
        </svg>
      );

    case 'methodology':
      // Layered protocol with check anchor — scientific stack
      return (
        <svg {...common}>
          <rect x="4" y="14" width="16" height="6" rx="1" />
          <rect x="4" y="8" width="16" height="4" rx="1" opacity="0.7" />
          <rect x="4" y="3" width="16" height="3" rx="1" opacity="0.5" />
          <path d="M9 17l1.5 1.5L15 14" />
        </svg>
      );

    case 'registry':
      // Ledger lines with anchor circle (serial)
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="16" rx="1.5" />
          <line x1="7" y1="9" x2="17" y2="9" />
          <line x1="7" y1="13" x2="14" y2="13" />
          <line x1="7" y1="17" x2="12" y2="17" />
          <circle cx="17" cy="17" r="2" />
        </svg>
      );

    case 'verification':
      // Magnifier over document
      return (
        <svg {...common}>
          <path d="M5 3h10l4 4v14H5z" />
          <circle cx="11" cy="13" r="3" />
          <line x1="13.2" y1="15.2" x2="15.5" y2="17.5" />
        </svg>
      );

    case 'monitoring':
      // Satellite over waveform
      return (
        <svg {...common}>
          <rect x="9" y="3" width="6" height="6" rx="0.5" />
          <path d="M7 6h-3M20 6h-3M12 9v3" />
          <path d="M2 18c2-1 4-1 5 0s4 1 5 0 4-1 5 0 3 1 5 0" />
          <circle cx="12" cy="6" r="1" fill="currentColor" />
        </svg>
      );

    case 'biodiversity':
      // Network of nodes — species/habitat links
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="2" />
          <circle cx="18" cy="6" r="2" />
          <circle cx="6" cy="18" r="2" />
          <circle cx="18" cy="18" r="2" />
          <circle cx="12" cy="12" r="2.5" />
          <line x1="7.5" y1="7.5" x2="10" y2="10" />
          <line x1="16.5" y1="7.5" x2="14" y2="10" />
          <line x1="7.5" y1="16.5" x2="10" y2="14" />
          <line x1="16.5" y1="16.5" x2="14" y2="14" />
        </svg>
      );

    case 'mangrove':
      // Stylised mangrove root system
      return (
        <svg {...common}>
          <path d="M12 3v9" />
          <path d="M12 8c-2 0-3 1.5-3 3M12 8c2 0 3 1.5 3 3" />
          <path d="M6 21c0-3 2-4 3.5-5M18 21c0-3-2-4-3.5-5M12 12v9" />
          <path d="M3 21h18" opacity="0.5" />
        </svg>
      );

    case 'kelp':
      // Vertical kelp blades rising
      return (
        <svg {...common}>
          <path d="M7 21V8c0-2 1-3 1.5-4M12 21V6c0-2 0.5-3 1-4M17 21V9c0-2-1-3-1.5-5" />
          <path d="M3 21h18" opacity="0.5" />
          <ellipse cx="8" cy="9" rx="1" ry="2" opacity="0.6" />
          <ellipse cx="13" cy="7" rx="1" ry="2" opacity="0.6" />
          <ellipse cx="17" cy="11" rx="1" ry="2" opacity="0.6" />
        </svg>
      );

    case 'seagrass':
      // Bent seagrass blades on seabed
      return (
        <svg {...common}>
          <path d="M5 21c0-5 1-7 2-9M9 21c0-6 1-8 2-10M13 21c0-6 1-8 2-10M17 21c0-5 1-7 2-9" />
          <path d="M3 21h18" />
        </svg>
      );

    case 'coastal':
      // Coastline meeting waves
      return (
        <svg {...common}>
          <path d="M2 15h7l1-3 2 5 2-7 1 5h7" />
          <path d="M2 19c2-1 3-1 4 0s3 1 4 0 3-1 4 0 3 1 4 0" opacity="0.7" />
        </svg>
      );

    case 'permanence':
      // Anchor / buffer reserve
      return (
        <svg {...common}>
          <circle cx="12" cy="6" r="2" />
          <line x1="12" y1="8" x2="12" y2="20" />
          <path d="M5 14c0 4 3 6 7 6s7-2 7-6" />
          <line x1="9" y1="11" x2="15" y2="11" />
        </svg>
      );

    case 'transparency':
      // Window-pane / glass panel with ledger lines
      return (
        <svg {...common}>
          <rect x="3" y="3" width="18" height="18" rx="1.5" />
          <line x1="3" y1="12" x2="21" y2="12" opacity="0.5" />
          <line x1="12" y1="3" x2="12" y2="21" opacity="0.5" />
          <path d="M7 17l2-2 2 1 3-4 3 2" opacity="0.8" />
        </svg>
      );

    case 'safeguards':
      // People silhouette in protective frame
      return (
        <svg {...common}>
          <path d="M12 3l8 3v5c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6z" />
          <circle cx="12" cy="11" r="2" />
          <path d="M8.5 17c0.7-1.5 2-2.5 3.5-2.5s2.8 1 3.5 2.5" />
        </svg>
      );

    case 'data':
      // Sample / sensor signal
      return (
        <svg {...common}>
          <rect x="9" y="3" width="6" height="11" rx="1" />
          <line x1="12" y1="14" x2="12" y2="20" />
          <circle cx="12" cy="20.5" r="1.5" fill="currentColor" />
          <path d="M3 8h3M18 8h3M3 12h3M18 12h3" opacity="0.7" />
        </svg>
      );

    case 'flow':
      // Lifecycle arrows
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="2" />
          <circle cx="18" cy="12" r="2" />
          <circle cx="6" cy="18" r="2" />
          <path d="M8 6.5c4 0 6 2.5 8 5" />
          <path d="M16 12.5c-4 0-6 2.5-8 5" />
          <polyline points="14.5,5 16,7 14,7.5" />
          <polyline points="10,15 8,17.5 10,18" />
        </svg>
      );

    case 'currents':
      // Smooth current lines
      return (
        <svg {...common}>
          <path d="M3 8c3-2 6-2 9 0s6 2 9 0" />
          <path d="M3 12c3-2 6-2 9 0s6 2 9 0" opacity="0.7" />
          <path d="M3 16c3-2 6-2 9 0s6 2 9 0" opacity="0.5" />
        </svg>
      );

    case 'reversal':
      // Triangle alert with declining line
      return (
        <svg {...common}>
          <path d="M12 3l9 16H3z" />
          <line x1="12" y1="9" x2="12" y2="14" />
          <circle cx="12" cy="17" r="0.5" fill="currentColor" />
        </svg>
      );

    case 'attest':
      // Stamped seal
      return (
        <svg {...common}>
          <circle cx="12" cy="10" r="6" />
          <path d="M9 10l2 2 4-4" />
          <path d="M8 16l-1 5 5-2 5 2-1-5" />
        </svg>
      );

    case 'serial':
      // Barcode-like serial pattern
      return (
        <svg {...common}>
          <line x1="4" y1="6" x2="4" y2="18" />
          <line x1="7" y1="6" x2="7" y2="18" strokeWidth={2.5} />
          <line x1="10" y1="6" x2="10" y2="18" />
          <line x1="13" y1="6" x2="13" y2="18" strokeWidth={2.5} />
          <line x1="16" y1="6" x2="16" y2="18" />
          <line x1="19" y1="6" x2="19" y2="18" strokeWidth={2.5} />
        </svg>
      );

    case 'governance':
      // Pillar / independent panel
      return (
        <svg {...common}>
          <path d="M3 7l9-4 9 4" />
          <line x1="3" y1="7" x2="21" y2="7" />
          <line x1="6" y1="10" x2="6" y2="18" />
          <line x1="12" y1="10" x2="12" y2="18" />
          <line x1="18" y1="10" x2="18" y2="18" />
          <line x1="3" y1="21" x2="21" y2="21" />
        </svg>
      );

    default:
      return null;
  }
}
